import { useState } from 'react';

/* ── guide section types ── */

interface GuideStep {
  title: string;
  description: string;
  tips?: string[];
}

interface GuideSection {
  id: string;
  title: string;
  summary: string;
  steps: GuideStep[];
}

/* ── guide content ── */

const GUIDE_SECTIONS: GuideSection[] = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    summary:
      'Follow these steps to set up your tenant and start accepting crypto payments through Discord.',
    steps: [
      {
        title: 'Create or join a tenant',
        description:
          'A tenant represents your Discord server on SubFi Pay. A Platform Admin must create a tenant for your server by providing the server name, Guild ID, and an owner. Once created, you\'ll be whitelisted and can access the dashboard.',
        tips: [
          'Your Discord Guild ID can be found in Discord under Server Settings → Widget → Server ID, or by enabling Developer Mode and right-clicking the server name.',
          'If you don\'t see any data in the dashboard, make sure a Platform Admin has whitelisted your Discord account for the correct tenant.',
        ],
      },
      {
        title: 'Set up your Discord bot token',
        description:
          'Go to Settings and enter your Discord bot token. This allows SubFi Pay to manage roles and send payment notifications in your server. The token is encrypted at rest and never exposed in the dashboard.',
        tips: [
          'Create a bot at discord.dev, enable the "Server Members" and "Message Content" privileged intents, then copy the bot token.',
          'Make sure the bot is invited to your server with the "Manage Roles" permission.',
        ],
      },
      {
        title: 'Configure your wallets',
        description:
          'In Settings, add your receiving wallet addresses for each chain you want to accept (Solana, USDT on BNB, Litecoin). Payments will be swept to these addresses.',
        tips: [
          'Double-check wallet addresses — payments sent to the wrong address cannot be recovered.',
        ],
      },
      {
        title: 'Create your first plan',
        description:
          'Go to the Plans page and click "+ New Plan". Set a name, price in USD, duration in days, the Discord role to grant on payment, and which chains to accept. Once created, users can subscribe through the Discord bot.',
        tips: [
          'The Discord Role ID is found by right-clicking a role in Server Settings → Roles (with Developer Mode enabled).',
          'You can create multiple plans for different tiers (e.g. Basic 30-day, Pro 90-day).',
        ],
      },
      {
        title: 'Configure embeds (optional)',
        description:
          'Customize how the payment embed looks in Discord. Set a title, description, color, footer text, and thumbnail image. This is what users see when they interact with the payment bot.',
      },
    ],
  },
  {
    id: 'overview',
    title: 'Overview',
    summary:
      'The Overview page gives you a high-level snapshot of your tenant\'s performance and recent activity.',
    steps: [
      {
        title: 'KPI cards',
        description:
          'The top row shows key metrics: total revenue, active subscribers, monthly recurring revenue (MRR), and churn rate. Each card includes a trend indicator comparing to the previous period.',
      },
      {
        title: 'Revenue chart',
        description:
          'The interactive chart shows revenue over time. Use the period filters (7d, 30d, 90d, All) to zoom in or out. Hover over data points for exact values.',
      },
      {
        title: 'Activity feed',
        description:
          'The right panel shows a real-time feed of recent events: new subscriptions, payments confirmed, renewals, and expirations. Click any entry to jump to its detail.',
      },
    ],
  },
  {
    id: 'plans',
    title: 'Plans',
    summary:
      'Plans define your subscription tiers — what users pay, how long access lasts, and which Discord role they receive.',
    steps: [
      {
        title: 'Create a plan',
        description:
          'Click "+ New Plan" and fill in the form: Name (display name), Label (shown in Discord), Emoji, Price in USD, Duration in days, Discord Role ID, and which chains to accept. All fields are required.',
        tips: [
          'Common durations: 30 days (monthly), 90 days (quarterly), 365 days (yearly).',
          'You can enable multiple chains per plan — users choose which to pay with.',
        ],
      },
      {
        title: 'Edit a plan',
        description:
          'Click "Edit" on any plan card to update its details. Changes take effect immediately for new subscribers. Existing subscribers keep their current terms until renewal.',
      },
      {
        title: 'Deactivate a plan',
        description:
          'Click "Delete" on a plan card to deactivate it. This hides the plan from new subscribers but does not affect existing subscriptions. Deactivated plans cannot be reactivated — create a new plan instead.',
      },
      {
        title: 'Renewal settings',
        description:
          'Below the plan cards, you\'ll see renewal configuration: auto-renewal reminders (DM users before expiry), grace period (allow late payments), and sweep mode (how confirmed payments reach your wallet). These are configured per-tenant in Settings.',
      },
    ],
  },
  {
    id: 'users',
    title: 'Users',
    summary:
      'The Users page shows all subscribers across your active plans, with filtering and search.',
    steps: [
      {
        title: 'Subscriber list',
        description:
          'View all users who have subscribed to any plan on your tenant. Each row shows the Discord username, plan, status (active/expired/grace), start date, and expiry date.',
      },
      {
        title: 'Filter and search',
        description:
          'Use the filter pills to narrow by status (Active, Expired, Grace Period). The search box filters by Discord username. Combine both to quickly find specific users.',
      },
      {
        title: 'User details',
        description:
          'Click a user row to see their full history: all payments made, plan changes, and renewal activity. This is useful for support inquiries.',
      },
    ],
  },
  {
    id: 'payments',
    title: 'Payments',
    summary:
      'Track every payment session and its lifecycle from creation to confirmation.',
    steps: [
      {
        title: 'Payment sessions',
        description:
          'Each time a user initiates a payment, a session is created with a unique wallet address. The session tracks the expected amount, chain, and current status (pending, confirming, confirmed, expired, failed).',
      },
      {
        title: 'Audit journal',
        description:
          'Every state change is logged in the audit journal with a timestamp. This provides a complete record for accounting and dispute resolution.',
      },
      {
        title: 'Status filters',
        description:
          'Filter payments by status to focus on what needs attention. "Pending" shows sessions awaiting payment, "Confirming" shows transactions being validated on-chain, and "Confirmed" shows completed payments.',
        tips: [
          'If a payment shows "Expired", the user did not send funds within the session window. They\'ll need to initiate a new payment.',
        ],
      },
    ],
  },
  {
    id: 'embeds',
    title: 'Embeds',
    summary:
      'Customize the Discord embed that users see when interacting with the payment bot.',
    steps: [
      {
        title: 'Embed configuration',
        description:
          'Set the embed title, description, accent color, footer text, and thumbnail image URL. The live preview on the right shows exactly how it will appear in Discord.',
      },
      {
        title: 'Using the embed',
        description:
          'Once configured, the bot uses this embed template when users run the subscribe command or interact with payment buttons in your server.',
        tips: [
          'Use Discord markdown in the description for rich formatting (bold, links, etc.).',
          'The color value is a decimal integer — use a color picker tool to convert hex to decimal.',
        ],
      },
    ],
  },
  {
    id: 'affiliates',
    title: 'Affiliates',
    summary:
      'Set up a referral program to reward users who bring in new subscribers. This feature must be enabled in Settings.',
    steps: [
      {
        title: 'Enable the affiliate program',
        description:
          'Go to Settings and toggle "Affiliate Program" on. Set the commission percentage — this is the portion of each referred payment that goes to the affiliate.',
      },
      {
        title: 'Add affiliates',
        description:
          'On the Affiliates page, click "+ Add Affiliate" and enter the Discord User ID of the person you want to make an affiliate. They\'ll receive a unique referral link.',
      },
      {
        title: 'Track referrals',
        description:
          'The Affiliates page shows each affiliate\'s referral count, total revenue generated, and commission earned. Use this to identify your top promoters.',
      },
    ],
  },
  {
    id: 'settings',
    title: 'Settings',
    summary:
      'Configure your tenant\'s core settings: bot connection, wallets, chains, renewal behavior, and more.',
    steps: [
      {
        title: 'Bot token',
        description:
          'Enter your Discord bot token to connect SubFi Pay to your server. The token is encrypted and stored securely. You can update it at any time if you rotate tokens.',
      },
      {
        title: 'Receiving wallet',
        description:
          'Add your Solana wallet address where you want to receive payments. All payments are processed on the Solana blockchain.',
      },
      {
        title: 'Sweep mode',
        description:
          'Choose how confirmed payments are transferred to your wallet. "Auto-sweep" moves funds immediately upon confirmation. "Manual-sweep" holds funds until you trigger a sweep.',
      },
      {
        title: 'Renewal settings',
        description:
          'Configure auto-renewal behavior: enable/disable renewal reminders, set how many days before expiry to send DMs, and configure the grace period (hours after expiry where late payments are still accepted).',
        tips: [
          'A grace period of 24–48 hours is recommended to account for timezone differences and delayed transactions.',
        ],
      },
    ],
  },
];

/* ── component ── */

export function GuidePage() {
  const [activeSection, setActiveSection] = useState('getting-started');
  const section = GUIDE_SECTIONS.find((s) => s.id === activeSection)!;

  return (
    <>
      <div style={{ marginBottom: 20 }}>
        <h2
          style={{
            fontFamily: 'var(--fd)',
            fontSize: 18,
            fontWeight: 700,
            letterSpacing: '-0.02em',
            marginBottom: 4,
          }}
        >
          Guide
        </h2>
        <p style={{ fontSize: 13, color: 'var(--t3)' }}>
          Learn how to set up and manage your SubFi Pay dashboard
        </p>
      </div>

      {/* tab bar */}
      <div className="filter-row" style={{ marginBottom: 24, flexWrap: 'wrap' }}>
        {GUIDE_SECTIONS.map((s) => (
          <div
            key={s.id}
            className={`filter-pill ${activeSection === s.id ? 'active' : ''}`}
            onClick={() => setActiveSection(s.id)}
          >
            {s.title}
          </div>
        ))}
      </div>

      {/* section header */}
      <div className="guide-section-header" key={section.id}>
        <h3
          style={{
            fontFamily: 'var(--fd)',
            fontSize: 20,
            fontWeight: 700,
            letterSpacing: '-0.02em',
            marginBottom: 6,
          }}
        >
          {section.title}
        </h3>
        <p style={{ fontSize: 14, color: 'var(--t2)', lineHeight: 1.6 }}>
          {section.summary}
        </p>
      </div>

      {/* step cards */}
      {section.steps.map((step, i) => (
        <div
          key={`${section.id}-${i}`}
          className="guide-step-card"
          style={{ animationDelay: `${0.05 + i * 0.05}s` }}
        >
          <div className="guide-step-number">{i + 1}</div>
          <div className="guide-step-content">
            <h4>{step.title}</h4>
            <p>{step.description}</p>
            {step.tips && (
              <div className="guide-tips">
                {step.tips.map((tip, j) => (
                  <div key={j} className="guide-tip">
                    {tip}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ))}
    </>
  );
}
